import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Modal from "../../components/Modal";
import PrintPreviewModal from "../../components/PrintPreviewModal";
import Layout from "../../components/Layout";
import {
  faSort,
  faSortUp,
  faSortDown,
  faAddressCard,
  faCircleCheck,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useUser } from "../../contexts/UserContext"; // UserContext をインポート
import ConfirmModal from "../../components/ConfirmModal";
import SuccessModal from "../../components/SuccessModal";
import ErrorModal from "../../components/ErrorModal";

type User = {
  id: number;
  employee_id: string;
  last_name: string;
  first_name: string;
  last_name_en: string;
  first_name_en: string;
  name_ini: string;
  name: string;
  name_en: string;
  nationality: string;
  gender: string;
  age: number;
  birth_date: string;
  current_address: string;
  department_name?: string;
  qualifications?: {
    id: string;
    name: string;
    pivot: {
      qualification_id: number;
      qualification_get_date: string;
    };
  }[];
  sheets: {
    updated_at: string;
  };
  isSelected: boolean;
  isTarget: boolean;
};

type Qualification = {
  id: string | "";
  qualification_id: string | null;
  name: string | null;
  get_date: string | null;
  isDisplay: boolean;
};

type TempView = {
  id: number;
  target_name: string;
  viewer_name: string;
  start_date: string;
  end_date: string;
  isSelected: boolean;
};
type PostTempView = {
  view_user_id: number;
  target_user_id: number;
};

const UserSheetsPage: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [tempViews, setTempViews] = useState<TempView[]>([]);
  const [loading, setLoading] = useState(false); // ロード中の状態を管理
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const { authUser } = useUser(); // UserContext からユーザー情報を取得
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedQualifications, setSelectedQualifications] = useState<
    Qualification[]
  >([]);
  const [isConfirmModalOpen, setConfirmModalOpen] = useState(false); // 確認モーダル
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false); // 完了モーダル
  const [isErrorModalOpen, setErrorModalOpen] = useState(false); // エラーモーダル
  const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
  const [modalText, setModalText] = useState("");

  //userのisSales情報をUserContextから取得
  let isSales = !!authUser?.isTfSales;

  //user情報を取得
  const fetchUsers = async () => {
    try {
      setLoading(true); // ロード開始
      const response = await axios.get<User[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/tf/user-sheets/users`,
        {
          withCredentials: true, // Cookieを自動的に送信
        }
      );
      const sortedUserData = [...response.data].sort((a, b) => {
        if (a.employee_id == null) return 1;
        if (b.employee_id == null) return -1;
        return String(a.employee_id).localeCompare(String(b.employee_id));
      });
      setUsers(sortedUserData);
    } catch (error) {
      console.error("ユーザー取得エラー:", error);
      setErrorModalOpen(true);
    } finally {
      setLoading(false); // ロード終了
    }
  };

  //一時閲覧情報を取得
  const fetchTempViews = async () => {
    try {
      setLoading(true); // ロード開始
      const response = await axios.get<TempView[]>(
        `${process.env.REACT_APP_BACKEND_URL}/api/tf/user-sheets/temp-views`,
        {
          withCredentials: true, // Cookieを自動的に送信
        }
      );
      const addIsSelectedField = response.data.map((item) => ({
        ...item,
        isSelected: false,
      }));
      setTempViews(addIsSelectedField);
    } catch (error) {
      console.error("ユーザー取得エラー:", error);
      setErrorModalOpen(true);
    } finally {
      setLoading(false); // ロード終了
    }
  };

  useEffect(() => {
    document.title = "スキルマネジメント -スキルシート共有";
    fetchUsers();

    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(today.getDate() + 7);
    setStartDate(today.toISOString().split("T")[0]); // 初期値として本日を設定
    setEndDate(nextWeek.toISOString().split("T")[0]); // 初期値として一週間後を設定
  }, []);

  const toggleUserSelected = (id: number) => {
    setUsers(
      users.map((user) =>
        user.id === id ? { ...user, isSelected: !user.isSelected } : user
      )
    );
  };

  const toggleTempViewSelected = (id: number) => {
    setTempViews(
      tempViews.map((tempView) =>
        tempView.id === id
          ? { ...tempView, isSelected: !tempView.isSelected }
          : tempView
      )
    );
  };

  const toggleAllViewPermissions = () => {
    const allSelected = users.every((user) => user.isSelected);
    setUsers(users.map((user) => ({ ...user, isSelected: !allSelected })));
  };

  const toggleAllEditPermissions = () => {
    const allTarget = users.every((user) => user.isTarget);
    setUsers(users.map((user) => ({ ...user, isTarget: !allTarget })));
  };

  const toggleAllTemps = () => {
    const allSelected = sortedTempViews.every((temp) => temp.isSelected);
    setTempViews(
      tempViews.map((temp) => ({ ...temp, isSelected: !allSelected }))
    );
  };

  const toggleUserisTarget = (id: number) => {
    setUsers(
      users.map((user) =>
        user.id === id ? { ...user, isTarget: !user.isTarget } : user
      )
    );
  };

  const [sortConfig, setSortConfig] = useState<{
    key: keyof User;
    direction: "asc" | "desc" | null;
  }>({
    key: "id",
    direction: null,
  });

  // sortedUsers関数でkeyof Userの直接のプロパティ以外を扱うための関数
  const getNestedValue = (obj: any, property: string) => {
    const keys = property.split(".");
    let value = obj;
    for (let key of keys) {
      if (value && value.hasOwnProperty(key)) {
        value = value[key];
      } else {
        return undefined;
      }
    }
    return value;
  };

  const sortedUsers = [...users].sort((a, b) => {
    if (!sortConfig.key || sortConfig.direction === null) return 0;

    let valueA, valueB;
    // 並び替えの対象がUserの直接のプロパティか否かで場合分け
    if (!sortConfig.key.includes(".")) {
      valueA = a[sortConfig.key];
      valueB = b[sortConfig.key];
    } else {
      valueA = getNestedValue(a, sortConfig.key as string);
      valueB = getNestedValue(b, sortConfig.key as string);
    }

    // 値が undefined または null の場合の処理
    if (valueA == null && valueB == null) return 0; // 両方が null または undefined の場合
    if (valueA == null) return sortConfig.direction === "asc" ? -1 : 1; // 片方が null または undefined の場合
    if (valueB == null) return sortConfig.direction === "asc" ? 1 : -1;

    // 文字列として比較
    if (valueA < valueB) return sortConfig.direction === "asc" ? -1 : 1;
    if (valueA > valueB) return sortConfig.direction === "asc" ? 1 : -1;

    return 0;
  });

  const requestSort = (key: keyof User) => {
    let direction: "asc" | "desc" | null = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc")
      direction = "desc";
    setSortConfig({ key, direction });
  };

  const getSortIcon = (key: keyof User) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? faSortUp : faSortDown;
    }
    return faSort;
  };

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isSharedListModalOpen, setIsSharedListModalOpen] = useState(false);

  const openShareModal = () => {
    updateFieldsToFalse(["isSelected", "isTarget"]);
    setIsShareModalOpen(true);
  };

  const openSharedListModal = () => {
    setIsSharedListModalOpen(true);
    fetchTempViews();
  };

  // スタート日とエンド日を比較して、エンド日を更新
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStartDate = e.target.value;
    setStartDate(newStartDate);

    // 「終了日なし」のチェック判定/新しいスタート日とエンド日を比較
    if (!noEndDate && new Date(newStartDate) > new Date(endDate)) {
      // スタート日がエンド日より後の場合、エンド日をスタート日の翌日に変更
      const nextDay = new Date(newStartDate);
      nextDay.setDate(nextDay.getDate() + 7);
      setEndDate(nextDay.toISOString().split("T")[0]);
    }
  };

  // 「終了日なし」チェックボックスの追加
  const [noEndDate, setNoEndDate] = useState(false);

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (noEndDate) return; // チェックされていれば編集不可
    setEndDate(e.target.value);
  };

  // selectedがtrueのユーザー
  const selectedUsers = users.filter((user) => user.isSelected);
  // targetがtrueのユーザー
  const targetUsers = users.filter((user) => user.isTarget);

  const checkCheckbox = () => {
    if (selectedUsers.length > 0) {
      if (targetUsers.length > 0) {
        return saveTempView();
      }
    }
  };

  const saveTempView = () => {
    if (selectedUsers.length > 0 && targetUsers.length > 0) {
      setModalText(
        "選択した設定の閲覧・編集権限を付与します。\n保存しますか。"
      );
      setConfirmAction(() => async () => {
        try {
          setLoading(true);
          const combination = selectedUsers.flatMap((selectedUser) =>
            targetUsers.map((targetUser) => ({
              view_user_id: selectedUser.id,
              target_user_id: targetUser.id,
              start_date: startDate,
              end_date: endDate,
            }))
          );
          await axios.post(
            `${process.env.REACT_APP_BACKEND_URL}/api/tf/user-sheets/update-temp-views`,
            { update_temp_views: combination },
            { withCredentials: true }
          );
          setSuccessModalOpen(true);
          setIsShareModalOpen(false);
        } catch (error) {
          console.error("保存エラー:", error);
          setErrorModalOpen(true);
        } finally {
          setLoading(false);
        }
      });
      setConfirmModalOpen(true);
    }
  };

  const updateFieldsToFalse = (fields: ("isSelected" | "isTarget")[]) => {
    // 指定されたフィールドをすべて false に更新
    const updatedUsers = users.map((user) => {
      const updatedUser = { ...user };
      fields.forEach((field) => {
        updatedUser[field] = false;
      });
      return updatedUser;
    });

    setUsers(updatedUsers); // 更新後の配列をセット
  };

  const sortedTempViews = [...tempViews].sort((a, b) => b.id - a.id); // 並び替え

  const selectedTempViewIds = tempViews
    .filter((tempView) => tempView.isSelected) // isSelectedがtrueの項目のみフィルタリング
    .map((tempView) => tempView.id); // IDを抽出

  const handleDeleteTempView = () => {
    if (selectedTempViewIds.length > 0) {
      setModalText("選択した共有情報を削除しますか？");
      setConfirmAction(() => async () => {
        try {
          setLoading(true);
          await axios.delete(
            `${process.env.REACT_APP_BACKEND_URL}/api/tf/user-sheets/delete-temp-views`,
            {
              params: { delete_temp_views: selectedTempViewIds },
              withCredentials: true,
            }
          );
          await fetchTempViews();
          setSuccessModalOpen(true);
        } catch (error) {
          console.error("削除エラー:", error);
          setErrorModalOpen(true);
        } finally {
          setLoading(false);
        }
      });
      setConfirmModalOpen(true);
    }
  };

  // 印刷オプション入力モーダルを開く
  const openPrevModal = (selectedUser: User) => {
    setSelectedUser(selectedUser);
    // ユーザーの資格情報を分離してセット（資格情報がある場合のみ）
    const formattedQualifications =
      selectedUser.qualifications?.map((q) => ({
        id: q.id,
        qualification_id: q.pivot.qualification_id.toString(),
        name: q.name,
        get_date: q.pivot.qualification_get_date,
        isDisplay: true,
      })) || [];
    setSelectedQualifications(formattedQualifications);
    setModalOpen(true);
  };

  return (
    <Layout>
      <div className="p-10 bg-white rounded-3xl min-h-[85vh]">
        {loading ? (
          // スケルトンスクリーン
          <>
            <div className="flex mb-4">
              <div className="border-l-8 rounded-3xl border-slate-100 mr-4"></div>
              <div className="w-48 h-6 bg-slate-200 rounded animate-pulse"></div>
              <div className="text-lg text-transparent select-none">
                レイアウトの為の透明文字
              </div>
            </div>
            <div className="flex items-center justify-end mb-4">
              <span className="py-2 px-4 text-transparent select-none">
                レイアウトの為の透明文字
              </span>
            </div>
            <div className="overflow-auto border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <div className="p-6 space-y-4">
                {Array.from({ length: 10 }).map((_, index) => (
                  <div key={index} className="flex space-x-4">
                    {isSales && (
                      <div className="w-8 h-8 bg-slate-200 rounded animate-pulse"></div>
                    )}
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                    <div className="flex-1 h-8 bg-slate-200 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
            </div>
          </>
        ) : (
          // 実データ表示
          <>
            <div className="flex mb-4">
              <div className="title-text font-black">スキルシート一覧</div>
            </div>
            {isSales ? (
              <div className="flex items-center justify-end mb-4">
                <button
                  onClick={() => openSharedListModal()}
                  className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 ml-4"
                >
                  <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                  一時共有一覧
                </button>
                <button
                  onClick={() => openShareModal()}
                  className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 ml-2"
                >
                  <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                  一時共有追加
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-end mb-4">
                <span className="py-2 px-4 text-transparent select-none">
                  レイアウトの為の透明文字
                </span>
              </div>
            )}
            <div className="overflow-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl">
              <table className="min-w-full text-slate-600 border-collapse table-auto">
                <thead className="sticky top-0 bg-slate-100 border-b-2 border-slate-300">
                  <tr>
                    {[
                      { key: "employee_id", label: "社員番号" },
                      { key: "name", label: "氏名" },
                      { key: "name_en", label: "氏名（英）" },
                      { key: "department_name", label: "所属" },
                      { key: "sheets.updated_at", label: "変更履歴" },
                    ].map((column) => (
                      <th
                        key={column.key}
                        onClick={() => requestSort(column.key as keyof User)}
                        className="py-2 px-4 text-center"
                      >
                        <div>
                          {column.label}
                          <FontAwesomeIcon
                            icon={getSortIcon(column.key as keyof User)}
                            className="ml-2"
                          />
                        </div>
                      </th>
                    ))}
                    <th className="py-2 px-4 border-b">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((user, index) => (
                    <tr
                      key={user.id}
                      className={`${
                        index % 2 === 0 ? "bg-white" : "bg-slate-50"
                      } hover:bg-[#E4F4F7] cursor-pointer text-center`}
                    >
                      <td className="py-2 px-4 border-b">{user.employee_id}</td>
                      <td className="py-2 px-4 border-b">{user.name}</td>
                      <td className="py-2 px-4 border-b">{user.name_en}</td>
                      <td className="py-2 px-4 border-b">
                        {user.department_name || "未設定"}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {user.sheets?.updated_at || "未作成"}
                      </td>
                      <td className="py-2 px-4 border-b space-x-2">
                        <button
                          onClick={() => openPrevModal(user)}
                          disabled={!user.sheets?.updated_at}
                          className={`font-bold py-1 px-4 rounded-3xl ${
                            user.sheets?.updated_at
                              ? "bg-gray-700 text-white hover:bg-opacity-50"
                              : "bg-gray-300 text-gray-500 cursor-not-allowed"
                          }`}
                        >
                          <FontAwesomeIcon icon={faPrint} className="mr-2" />
                          印刷プレビュー
                        </button>

                        <button
                          onClick={() => navigate(`/tf/my-sheet/${user.id}`)}
                          className="bg-slate-700 text-white font-bold py-1 px-4 rounded-3xl hover:bg-opacity-50"
                        >
                          編集
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* 一時共有をするモーダル */}
            <Modal
              isOpen={isShareModalOpen}
              onClose={() => setIsShareModalOpen(false)}
              title="一時共有"
            >
              <div>
                <div>
                  <div className="flex space-x-4">
                    {/* 開始日 */}
                    <div>
                      <label
                        htmlFor="start-date"
                        className="block text-sm font-medium text-gray-700"
                      >
                        開始日
                      </label>
                      <input
                        type="date"
                        id="start-date"
                        value={startDate || ""}
                        onChange={(e) => handleStartDateChange(e)}
                        className="mt-1 mb-4 block w-32 border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>

                    {/* 終了日 */}
                    <div>
                      <label
                        htmlFor="end-date"
                        className="block text-sm font-medium text-gray-700"
                      >
                        終了日
                      </label>
                      <input
                        type="date"
                        id="end-date"
                        value={endDate || ""}
                        onChange={(e) => handleEndDateChange(e)}
                        disabled={noEndDate}
                        className="mt-1 mb-4 block w-32 border border-gray-300 rounded-md shadow-sm p-2 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div className="flex items-center space-x-2 mb-4 mt-7">
                      <div className="relative inline-block">
                        <input
                          id="no-end-date"
                          type="checkbox"
                          checked={noEndDate}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setNoEndDate(checked);

                            if (checked) {
                              setEndDate("9999-12-31");
                            } else {
                              const base = startDate
                                ? new Date(startDate)
                                : new Date();
                              base.setDate(base.getDate() + 7);
                              setEndDate(base.toISOString().split("T")[0]);
                            }
                          }}
                          className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                        />
                        {noEndDate && (
                          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                            <img
                              src="/check-icon.svg"
                              alt="Check"
                              className="w-4 h-4"
                            />
                          </div>
                        )}
                      </div>
                      <label
                        htmlFor="no-end-date"
                        className="text-sm text-gray-700"
                      >
                        終了日なし
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="overflow-auto max-h-[70vh] border-2 border-slate-300 bg-slate-100 rounded-2xl mb-4">
                <table className="min-w-full text-slate-600 border-collapse table-auto">
                  <thead className="sticky top-0 bg-slate-100 border-b-2 border-slate-300">
                    <tr>
                      {[
                        { key: "isSelected", label: "公開されるユーザー" },
                        { key: "isTarget", label: "閲覧・編集できるユーザ" },
                        { key: "employee_id", label: "社員番号" },
                        { key: "name", label: "氏名" },
                      ].map((column) => (
                        <th key={column.key} className="py-2 px-4 text-center">
                          <div className="flex items-center justify-center space-x-2">
                            {/* 一括選択用チェックボックス */}
                            {["isSelected", "isTarget"].includes(
                              column.key
                            ) && (
                              <div className="relative inline-block">
                                <input
                                  type="checkbox"
                                  checked={
                                    column.key === "isSelected"
                                      ? users.every((user) => user.isSelected)
                                      : users.every((user) => user.isTarget)
                                  }
                                  onChange={
                                    column.key === "isSelected"
                                      ? toggleAllViewPermissions
                                      : toggleAllEditPermissions
                                  }
                                  className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                                />
                                {(column.key === "isSelected"
                                  ? users.every((user) => user.isSelected)
                                  : users.every((user) => user.isTarget)) && (
                                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                    <img
                                      src="/check-icon.svg"
                                      alt="Check"
                                      className="w-4 h-4"
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                            <span>{column.label}</span>
                            <FontAwesomeIcon
                              icon={getSortIcon(column.key as keyof User)}
                              className="ml-2"
                              onClick={() =>
                                requestSort(column.key as keyof User)
                              }
                            />
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user, index) => (
                      <tr
                        key={user.id}
                        className={`${
                          index % 2 === 0 ? "bg-white" : "bg-slate-50"
                        } hover:bg-[#E4F4F7] cursor-pointer`}
                      >
                        <td className="py-2 px-4 border-b text-center">
                          <div
                            className="relative inline-block"
                            onClick={() => toggleUserSelected(user.id)}
                          >
                            <input
                              type="checkbox"
                              checked={user.isSelected}
                              readOnly
                              className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                            />
                            {user.isSelected && (
                              <div className="absolute inset-0 flex items-center justify-center">
                                <img
                                  src="/check-icon.svg"
                                  alt="Check"
                                  className="w-4 h-4"
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          <div
                            className="relative inline-block"
                            onClick={() => toggleUserisTarget(user.id)}
                          >
                            <input
                              type="checkbox"
                              checked={user.isTarget}
                              readOnly
                              className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                            />
                            {user.isTarget && (
                              <div
                                onClick={() => toggleUserisTarget(user.id)}
                                className="absolute inset-0 flex items-center justify-center"
                              >
                                <img
                                  src="/check-icon.svg"
                                  alt="Check"
                                  className="w-4 h-4"
                                />
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {user.employee_id}
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {user.name}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={checkCheckbox}
                  className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 "
                >
                  <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                  保存
                </button>
              </div>
            </Modal>

            {/* 一時共有一覧のモーダル */}
            <Modal
              isOpen={isSharedListModalOpen}
              onClose={() => setIsSharedListModalOpen(false)}
              title="一時共有一覧"
            >
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="mb-4">
                    閲覧ユーザーに共同編集権限ユーザーのスキルシートを指定期間共有しています。
                  </div>
                  <button
                    onClick={handleDeleteTempView}
                    className="bg-iso text-white font-bold py-2 px-4 rounded-3xl hover:bg-opacity-50 ml-2"
                  >
                    <FontAwesomeIcon icon={faCircleCheck} className="mr-2" />
                    削除
                  </button>
                </div>
                <div className="overflow-auto max-h-[60vh] border-2 border-slate-300 bg-slate-100 rounded-2xl mb-4">
                  <table className="min-w-full text-slate-600 border-collapse table-auto">
                    <thead className="sticky top-0 bg-slate-100 border-b-2 border-slate-300 z-10">
                      <tr>
                        <th className="py-2 px-4 text-center">
                          <div className="relative inline-block">
                            <input
                              type="checkbox"
                              checked={sortedTempViews.every(
                                (temp) => temp.isSelected
                              )}
                              onChange={toggleAllTemps}
                              className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                            />
                            {sortedTempViews.every(
                              (temp) => temp.isSelected
                            ) && (
                              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <img
                                  src="/check-icon.svg"
                                  alt="Check"
                                  className="w-4 h-4"
                                />
                              </div>
                            )}
                          </div>
                        </th>
                        {[
                          { key: "viewerName", label: "閲覧" },
                          { key: "targetName", label: "共同編集権限" },
                          { key: "startDate", label: "開始日" },
                          { key: "endDate", label: "終了日" },
                        ].map((column) => (
                          <th
                            key={column.key}
                            className="py-2 px-4 text-center"
                          >
                            <div>{column.label}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTempViews.map((tempViews, index) => (
                        <tr
                          key={tempViews.id}
                          className={`${
                            index % 2 === 0 ? "bg-white" : "bg-slate-50"
                          } hover:bg-[#E4F4F7] cursor-pointer`}
                          onClick={() => toggleTempViewSelected(tempViews.id)}
                        >
                          <td className="py-2 px-4 border-b text-center">
                            <div className="relative inline-block">
                              <input
                                type="checkbox"
                                checked={tempViews.isSelected}
                                readOnly
                                className="appearance-none w-6 h-6 border-2 border-slate-300 rounded-md cursor-pointer checked:bg-iso checked:border-iso"
                              />
                              {tempViews.isSelected && (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <img
                                    src="/check-icon.svg"
                                    alt="Check"
                                    className="w-4 h-4"
                                  />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {tempViews.viewer_name}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {tempViews.target_name}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {tempViews.start_date}
                          </td>
                          <td className="py-2 px-4 border-b text-center">
                            {tempViews.end_date}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </Modal>

            {/* 印刷プレビューオプション選択モーダル */}
            <PrintPreviewModal
              isOpen={isModalOpen}
              onClose={() => setModalOpen(false)}
              title="印刷プレビューオプション"
              user={selectedUser}
              qualifications={selectedQualifications}
              techFolio={null}
            ></PrintPreviewModal>
          </>
        )}
      </div>
      {/* 確認・成功・エラーモーダル */}
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        title="確認"
        text={modalText}
        onConfirm={() => {
          setConfirmModalOpen(false);
          confirmAction();
        }}
        onCancel={() => setConfirmModalOpen(false)}
      />
      <SuccessModal
        isOpen={isSuccessModalOpen}
        title="完了"
        text="処理が正常に完了しました。"
        onClose={() => setSuccessModalOpen(false)}
      />
      <ErrorModal
        isOpen={isErrorModalOpen}
        title="エラー"
        text="処理中にエラーが発生しました。再試行してください。"
        onClose={() => setErrorModalOpen(false)}
      />
    </Layout>
  );
};

export default UserSheetsPage;
